const colors = {
  'brown-100': '#f4f1f2',
  'brown-200': '#ece8ea',
  'brown-300': '#dad1d5',
  'brown-400': '#c7bbc0',
  'brown-500': '#a28d95',
  'brown-600': '#866b75',
  'brown-700': '#6a4956',
  'brown-800': '#451b2b',
  'brown-900': '#431527',
  'brown-1000': '#380c1d',
  'pink-100': '#fff1f7',
  'pink-200': '#ffe3ef',
  'pink-300': '#ffd4e5',
  'pink-400': '#ffc4dc',
  'pink-500': '#ff9cc5',
  'pink-600': '#ff75ae',
  'pink-700': '#ff589c',
  'pink-800': '#ff3a8b',
  'pink-900': '#ec2979',
  'pink-1000': '#da1968',
  'green-100': '#f3fbfa',
  'green-200': '#f3fbfa',
  'green-300': '#d5f0ed',
  'green-400': '#c3e9e5',
  'green-500': '#a6dfd9',
  'green-600': '#8cd5ce',
  'green-700': '#69c9bf',
  'green-800': '#4ebfb4',
  'green-900': '#39b4a8',
  'green-1000': '#0ba495',
  'red-800': '#d31510',
  'red-900': '#cb140f',
  'red-1000': '#c1120d',
  gradient: 'linear-gradient(90deg , #FF3A8B, #FFBA4A)',
  yellow: '#FFB24F',
  white: '#ffffff',
} as const;

const screens = {
  /** 320px */
  xs: '20.063rem',
  /** 640px */
  sm: '40.063rem',
  /** 768px */
  md: '48.063rem',
  /** 1024px */
  lg: '64.063rem',
  /** 1280px */
  xl: '80.063rem',
  /** 1600px */
  xxl: '100.063rem',
} as const;

// Don't forget to update this if you change the screen size
const breakpoints = {
  /** 321px */
  xs: `@media (width >= ${screens.xs})`,
  /** 641px */
  sm: `@media (width >= ${screens.sm})`,
  /** 769px */
  md: `@media (width >= ${screens.md})`,
  /** 1025px */
  lg: `@media (width >= ${screens.lg})`,
  /** 1281px */
  xl: `@media (width >= ${screens.xl})`,
  /** 1601px */
  xxl: `@media (width >= ${screens.xxl})`,
} as const;

const font = {
  family: {
    openSans: "'Open Sans', sans-serif'",
    sans: "'Noto Sans JP', sans-serif",
    serif: "'adobe-garamond-pro', serif",
  },
  weight: {
    normal: '400',
    semibold: '600',
    bold: '700',
  },
} as const;

const shadows = {
  l: '0px 10px 15px rgba(0,0,0,0.2)',
  m: '0px 5px 10px rgba(0,0,0,0.1)',
  s: '0px 1px 5px rgba(0,0,0,0.1)',
} as const;

// You can use this to calculate rem based on px:
// https://nekocalc.com/px-to-rem-converter
const spacings = {
  px1: '0.063rem',
  px2: '0.125rem',
  px3: '0.188rem',
  px4: '0.250rem',
  px5: '0.313rem',
  px6: '0.375rem',
  px8: '0.500rem',
  px7: '0.438rem',
  px9: '0.563rem',
  px10: '0.625rem',
  px11: '0.688rem',
  px12: '0.750rem',
  px13: '0.813rem',
  px14: '0.875rem',
  px15: '0.938rem',
  px16: '1.000rem',
  px18: '1.125rem',
  px19: '1.188rem',
  px20: '1.250rem',
  px21: '1.313rem',
  px22: '1.375rem',
  px24: '1.500rem',
  px25: '1.563rem',
  px28: '1.750rem',
  px30: '1.875rem',
  px32: '2.000rem',
  px36: '2.250rem',
  px38: '2.375rem',
  px40: '2.500rem',
  px44: '2.750rem',
  px45: '2.813rem',
  px48: '3.000rem',
  px50: '3.125rem',
  px52: '3.250rem',
  px55: '3.437rem',
  px56: '3.500rem',
  px59: '3.688rem',
  px60: '3.750rem',
  px62: '3.875rem',
  px64: '4.000rem',
  px67: '4.188rem',
  px72: '4.500rem',
  px78: '4.875rem',
  px80: '5.000rem',
  px85: '5.313rem',
  px86: '5.375rem',
  px90: '5.625rem',
  px96: '6.000rem',
  px100: '6.250rem',
  px112: '7.000rem',
  px120: '7.500rem',
  px128: '8.000rem',
  px130: '8.125rem',
  px140: '8.750rem',
  px150: '9.375rem',
  px180: '11.250rem',
  px193: '12.063rem',
  px200: '12.500rem',
  px216: '13.500rem',
  px220: '13.750rem',
  px230: '14.375rem',
  px235: '14.688rem',
  px238: '14.875rem',
  px240: '15.000rem',
  px260: '16.250rem',
  px280: '17.500rem',
  px300: '18.750rem',
  px305: '19.063rem',
  px316: '19.750rem',
  px335: '20.938rem',
  px340: '21.250rem',
  px375: '23.438rem',
  px410: '25.625rem',
  px480: '30.000rem',
  px500: '31.250rem',
  px600: '37.500rem',
  px635: '39.688rem',
  px365: '22.813rem',
  px650: '40.625rem',
  px693: '43.313rem',
  px700: '43.750rem',
  px945: '59.063rem',
  px930: '58.125rem',
  px1190: '74.375rem',
} as const;

export type Theme = {
  breakpoints: typeof breakpoints;
  colors: typeof colors;
  font: typeof font;
  screens: typeof screens;
  shadows: typeof shadows;
  spacings: typeof spacings;
};

/**
 * Represents the default theme configuration for the application.
 *
 * @constant
 * @type {Theme}
 * @property {object} colors - Defines the color palette used throughout the application.
 * @property {object} breakpoints - Specifies the responsive design breakpoints.
 *   Note: Currently, each breakpoint includes a +1px adjustment to align with the legacy system.
 *   This adjustment is temporary and will be removed in the future.
 * @property {object} font - Contains font-related configurations such as families, sizes, and weights.
 * @property {object} spacings - Defines spacing values converted from px to rem for consistency.
 *   If px values are required, they can be used directly as strings (e.g., `'20px'`).
 * @property {object} screens - Specifies screen size configurations for responsive design.
 * @property {object} shadows - Contains shadow definitions for UI elements.
 */
const THEME = {
  colors,
  /**
   * This will change in the future. We are using +1px for each breakpoint to adapt the old system.
   *
   * The +1px will to be removed.
   */
  breakpoints,
  font,
  /**
   * This is not in px, but rather a converted value from px to rem.
   *
   * If you want to use px directly, use it as a string (e.g., `borderWidth: '20px'`)
   */
  spacings,
  screens,
  shadows,
} as const satisfies Theme;

export default THEME;
